import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/header";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "@/i18n/LanguageContext";
import { useTitle } from "@/hooks/use-title";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface User {
  id: number;
  poolToken?: string;
  cacatais: number;
  youtube_member: boolean;
}

export default function PoolPage() {
  useTitle("Mining Pool");
  const [token, setToken] = useState("");
  const [cacatalPrice, setCacatalPrice] = useState<number | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: user } = useQuery<User>({
    queryKey: ["/api/auth/user"],
  });

  useEffect(() => {
    const fetchCacatalPrice = async () => {
      try {
        const response = await fetch('https://api.dexscreener.com/latest/dex/pairs/solana/9vcmegjcijqnkwnaxxwbkrjjqpj7d3sq5gcy4hzvwixd');
        const data = await response.json();
        if (data.pair) {
          setCacatalPrice(parseFloat(data.pair.priceUsd));
        }
      } catch (error) {
        console.error('Error fetching Cacatal price:', error);
      }
    };

    fetchCacatalPrice();
    // Refresh price every 5 minutes
    const interval = setInterval(fetchCacatalPrice, 300000);
    return () => clearInterval(interval);
  }, []);

  const verifyToken = useMutation({
    mutationFn: async (token: string) => {
      const res = await fetch("/api/pool/verify-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({ title: t("pool.tokenVerified") });
      setToken("");
    },
    onError: (error) => {
      toast({
        title: t("pool.invalidToken"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const generateToken = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/pool/generate-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({ title: "New token generated successfully" });
    },
    onError: (error) => {
      toast({
        title: "Failed to generate token",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <div className="container mx-auto pt-24 px-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">
              {user ? t("pool.title") : ""}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {user ? ( 
              <div className="space-y-6">
                {!user.poolToken ? (
                  <div className="space-y-4">
                    <div className="mb-4">
                      <Button onClick={() => generateToken.mutate()}>
                        Generate New Token
                      </Button>
                    </div>
                    <p className="text-zinc-400">
                      {t("pool.enterToken")}
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        placeholder={t("pool.poolToken")}
                        className="bg-zinc-800 border-zinc-700"
                      />
                      <Button
                        onClick={() => verifyToken.mutate(token)}
                        disabled={!token || verifyToken.isPending}
                      >
                        {t("pool.verify")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <p className="text-zinc-400">{t("pool.welcome")}</p>
                      <p className="text-zinc-400">
                        {t("pool.description")}
                      </p>
                      <div className="bg-zinc-800 p-4 rounded-lg space-y-2">
                        <p className="text-xl font-semibold text-white">
                          {t("pool.yourCacatais")}: {user.cacatais}
                          {cacatalPrice && (
                            <span className="block text-sm text-zinc-400">
                              ≈ ${(user.cacatais * cacatalPrice).toFixed(2)} USD
                            </span>
                          )}
                        </p>
                        <div className="space-y-1">
                          <p className="text-sm text-zinc-400">{t("pool.poolToken")}:</p>
                          <code className="block bg-zinc-950 p-2 rounded text-sm font-mono break-all">
                            {user.poolToken}
                          </code>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" className="mt-2">
                              Generate New Token
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                              <AlertDialogDescription>
                                The old token will stop working. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => generateToken.mutate()}>
                                Continue
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>

                    <Separator className="bg-zinc-800" />

                    <div className="space-y-6">
                      <h2 className="text-lg font-semibold text-white">{t("pool.apiDocs")}</h2>

                      {/* Regular Blocks API */}
                      <div className="space-y-4">
                        <h3 className="text-md font-semibold text-white">{t("pool.regularBlocks")}</h3>
                        <p className="text-zinc-400">{t("pool.regularBlocksDescription")}</p>

                        <div className="space-y-6">
                          <div>
                            <h4 className="text-sm font-semibold text-white mb-2">GET /api/block</h4>
                            <p className="text-zinc-400">
                              {t("pool.getBlockDescription")}
                            </p>
                            <div className="mt-2 space-y-2">
                              <p className="text-zinc-400">{t("pool.headers")}:</p>
                              <code className="block bg-zinc-800 p-2 rounded">
                                pool-token: your-pool-token
                              </code>
                            </div>
                            <div className="mt-2">
                              <p className="text-zinc-400">{t("pool.response")}:</p>
                              <pre className="bg-zinc-800 p-2 rounded mt-1 overflow-x-auto">
                                {`{
  "id": number,
  "status": 0,
  "range": {
    "start": "0x...",
    "end": "0x..."
  },
  "checkwork_addresses": string[],
  "message": string
}`}
                              </pre>
                            </div>
                          </div>

                          <div>
                            <h4 className="text-sm font-semibold text-white mb-2">POST /api/block</h4>
                            <p className="text-zinc-400">
                              {t("pool.postBlockDescription")}
                            </p>
                            <div className="mt-2 space-y-2">
                              <p className="text-zinc-400">{t("pool.headers")}:</p>
                              <code className="block bg-zinc-800 p-2 rounded">
                                pool-token: your-pool-token
                              </code>
                            </div>
                            <div className="mt-2">
                              <p className="text-zinc-400">{t("pool.requestBody")}:</p>
                              <pre className="bg-zinc-800 p-2 rounded mt-1 overflow-x-auto">
                                {`{
  "privateKeys": string[] // Array of 10 private keys
}`}
                              </pre>
                            </div>
                            <div className="mt-2">
                              <p className="text-zinc-400">{t("pool.successResponse")}:</p>
                              <pre className="bg-zinc-800 p-2 rounded mt-1 overflow-x-auto">
                                {`{
  "success": true,
  "message": "${t("pool.successResponseMessage")}"
}`}
                              </pre>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Big Blocks API */}
                      <div className="space-y-4">
                        <h3 className="text-md font-semibold text-white">{t("pool.bigBlocks")}</h3>
                        <p className="text-zinc-400">{t("pool.bigBlocksDescription")}</p>

                        <div className="space-y-6">
                          <div>
                            <h4 className="text-sm font-semibold text-white mb-2">GET /api/big_block</h4>
                            <p className="text-zinc-400">
                              {t("pool.getBigBlockDescription")}
                            </p>
                            <div className="mt-2 space-y-2">
                              <p className="text-zinc-400">{t("pool.headers")}:</p>
                              <code className="block bg-zinc-800 p-2 rounded">
                                pool-token: your-pool-token
                              </code>
                            </div>
                            <div className="mt-2">
                              <p className="text-zinc-400">{t("pool.response")}:</p>
                              <pre className="bg-zinc-800 p-2 rounded mt-1 overflow-x-auto">
                                {`{
  "id": number,
  "status": 0,
  "range": {
    "start": "0x...",  // 1T range instead of 1B
    "end": "0x..."
  },
  "checkwork_addresses": string[],
  "message": string
}`}
                              </pre>
                            </div>
                          </div>

                          <div>
                            <h4 className="text-sm font-semibold text-white mb-2">POST /api/big_block</h4>
                            <p className="text-zinc-400">
                              {t("pool.postBigBlockDescription")}
                            </p>
                            <div className="mt-2 space-y-2">
                              <p className="text-zinc-400">{t("pool.headers")}:</p>
                              <code className="block bg-zinc-800 p-2 rounded">
                                pool-token: your-pool-token
                              </code>
                            </div>
                            <div className="mt-2">
                              <p className="text-zinc-400">{t("pool.requestBody")}:</p>
                              <pre className="bg-zinc-800 p-2 rounded mt-1 overflow-x-auto">
                                {`{
  "privateKeys": string[] // Array of 10 private keys
}`}
                              </pre>
                            </div>
                            <div className="mt-2">
                              <p className="text-zinc-400">{t("pool.successResponse")}:</p>
                              <pre className="bg-zinc-800 p-2 rounded mt-1 overflow-x-auto">
                                {`{
  "success": true,
  "message": "${t("pool.successResponseMessage")}"
}`}
                              </pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6 text-zinc-400">
                <h2 className="text-2xl font-semibold text-white">{t("pool.welcomeTitle")}</h2>
                <p>{t("pool.welcomeDescription")}</p>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-4">{t("pool.howToParticipate")}</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>{t("pool.step1")}</li>
                    <li>{t("pool.step2")}</li>
                    <li>{t("pool.step3")}</li>
                  </ul>
                </div>
                <p className="text-lg font-medium">{t("pool.joinMessage")}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}